/**
 * 微信浏览器引导组件
 * 在微信内置浏览器中显示全屏蒙层，引导用户在外部浏览器打开
 */
import { Dialog } from '@base-ui-components/react/dialog';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useSiteConfig } from '@/contexts/GlobalProvider';

/**
 * 检测是否在微信内置浏览器中
 */
function isWechatBrowser(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('micromessenger');
}

/**
 * 微信浏览器引导蒙层组件
 * 显示全屏蒙层，提示用户点击右上角在外部浏览器打开
 */
export default function WechatBrowserGuide() {
  const { config } = useSiteConfig();

  // 直接在渲染时计算显示状态，避免 SSR/Hydration 闪烁问题
  const isClient = typeof window !== 'undefined';
  const isWechat = isClient && isWechatBrowser();
  const enabled = config?.wechatBrowserGuideEnabled ?? false;
  const shouldShow = isClient && enabled && isWechat;

  // 不显示时返回 null
  if (!shouldShow) {
    return null;
  }

  return (
    <Dialog.Root open={true}>
      <Dialog.Portal>
        {/* 全屏遮罩层 - 两层叠加 */}
        {/* 第一层：白色底层 */}
        <Dialog.Backdrop
          className={cn(
            'fixed inset-0 min-h-dvh bg-white z-[9997]',
            'transition-opacity duration-300',
            'data-[starting-style]:opacity-0',
            'data-[ending-style]:opacity-0'
          )}
        />
        {/* 第二层：黑色85%透明 */}
        <div className="fixed inset-0 min-h-dvh bg-black/85 z-[9998]" />

        {/* 引导内容 */}
        <Dialog.Popup
          className={cn(
            'fixed inset-0 z-[9999]',
            'flex flex-col items-center',
            'pt-4 px-6',
            'outline-none'
          )}
        >
          {/* 顶部箭头指向右上角 */}
          <div className="w-full flex justify-end pr-2">
            <div className="flex flex-col items-center animate-bounce-gentle">
              {/* 斜向右上角的箭头 */}
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                className="text-white"
              >
                <path
                  d="M12 28L28 12"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <path
                  d="M16 12H28V24"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {/* 提示内容区域 */}
          <div className="mt-16 flex flex-col items-center text-center">
            {/* 图标示意 */}
            <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
              <MoreHorizontal size={32} className="text-white" />
            </div>

            {/* 主标题 */}
            <h2 className="text-xl font-semibold text-white mb-4">
              请在浏览器中打开
            </h2>

            {/* 操作步骤 */}
            <div className="text-white/90 text-base leading-relaxed space-y-2">
              <p>点击右上角 <span className="font-medium">「•••」</span> 按钮</p>
              <p>选择 <span className="font-medium">「在浏览器中打开」</span></p>
            </div>

            {/* 补充说明 */}
            <p className="mt-8 text-white/60 text-sm">
              为了获得更好的使用体验，请使用系统浏览器访问
            </p>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
